import type { AlpacaEvent } from '../models/AlpacaEvent';
import type { Event } from '../models/Event';
import { useSearchParams } from 'react-router';
import EventApi from '../apis/event_api';
import { handleError } from '../apis';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { eventKeys } from '../queries/EventQueries';
import { useAlpacasDataOnly } from '../queries/AlpacaQueries';
import { useFarms } from '../queries/FarmQueries';
import { useMemo, useState, type HTMLProps } from 'react';
import type { Farm } from '../models/Farm';
import IndeterminateCheckbox from './IndeterminateCheckbox';

export default function AddAlpacaToEventForm({ event }: { event: Event }) {
  const queryClient = useQueryClient();
  const [, setSearchParams] = useSearchParams();
  const [selectBy, setSelectBy] = useState<string>('All Alpacas');
  const { data: alpacas } = useAlpacasDataOnly();
  const { data: farms = [] } = useFarms('');
  const [selectedFarmId, setSelectedFarmId] = useState<string>();
  const [selectedAlpacas, setSelectedAlpacas] = useState<number[]>([]);

  const alpacaEvent: AlpacaEvent = {
    alpacaEventId: 0,
    eventId: event?.eventId ?? 0,
    applicationUserId: event?.applicationUserId ?? '',
    alpacaId: 0,
    note: '',
  };

  const availableAlpacas = useMemo(() => {
    if (!alpacas || !event) return [];
    const addedIds = event?.alpacasForEvent?.map((a) => a.alpacaId);
    return (
      alpacas
        ?.filter((a) => !addedIds?.includes(a.alpacaId!))
        .sort((a, b) => a.name.localeCompare(b.name)) ?? []
    );
  }, [alpacas, event]);

  const alpacaSelections = useMemo(() => {
    if (!availableAlpacas) return [];
    if (selectBy === 'By Farm') {
      const farmId = selectedFarmId ? +selectedFarmId : (farms[0]?.farmId ?? 0);
      return (
        availableAlpacas
          ?.filter((a) => a.farmId === farmId)
          .sort((a, b) => a.name.localeCompare(b.name)) ?? []
      );
    }
    return availableAlpacas.sort((a, b) => a.name.localeCompare(b.name)) ?? [];
  }, [availableAlpacas, selectBy, selectedFarmId, farms]);

  const checked = useMemo(() => {
    return availableAlpacas.length > 0
      ? selectedAlpacas.length === availableAlpacas.length
      : false;
  }, [selectedAlpacas, availableAlpacas]);

  const indeterminate = useMemo(() => {
    return (
      selectedAlpacas.length > 0 &&
      selectedAlpacas.length < availableAlpacas.length
    );
  }, [selectedAlpacas, availableAlpacas]);

  const selectAllChange = (props: HTMLProps<HTMLInputElement>) => {
    if (props.checked) {
      const toAdd = alpacaSelections.map((a) => a.alpacaId!);

      setSelectedAlpacas((prev) => [...new Set([...prev, ...toAdd])]);
    } else {
      setSelectedAlpacas([]);
    }
  };

  const handleSelectBy = (val: string) => {
    setSelectBy(val);
  };
  const handleFarm = (val: string) => {
    setSelectedFarmId(val);
  };

  const onHandleSubmit = async () => {
    if (selectedAlpacas.length === 0) {
      toast.error('Please select at least one alpaca');
      return;
    }

    const createdEvents: AlpacaEvent[] = selectedAlpacas.map((alpacaId) => ({
      ...alpacaEvent,
      alpacaId,
    }));

    try {
      await EventApi.createAlpacaEvents(event.eventId, createdEvents);
      queryClient.invalidateQueries({
        queryKey: eventKeys.all,
      });
    } catch (error) {
      console.log(error);
      const message = handleError(error);
      toast.error(message);
    }
  };

  return (
    <div>
      <div className='mb-4 flex flex-wrap items-center justify-start gap-2 max-sm:justify-center'>
        <div className='mb-2 py-2'>
          <label
            htmlFor='selectAlpacasBy'
            className='flex justify-between text-xs font-bold uppercase'
          >
            Select By
          </label>
          <select
            id='selectAlpacasBy'
            name='selectAlpacasBy'
            value={selectBy}
            onChange={(e) => handleSelectBy(e.target.value)}
            className={`g-gray-50 mt-1 block w-full min-w-[275px]  rounded-lg border border-gray-500 p-2.5
                  text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500
                   dark:border-0 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:ring-gray-400
                     `}
          >
            {['All Alpacas', 'By Farm'].map((s) => (
              <option value={s} key={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {selectBy === 'By Farm' && (
          <div className='mb-2 py-2'>
            <label
              htmlFor='selectAlpacasBy'
              className='flex justify-between text-xs font-bold uppercase'
            >
              Farm
            </label>
            <select
              id='farmId'
              name='farmId'
              value={selectedFarmId}
              onChange={(e) => handleFarm(e.target.value)}
              className={`g-gray-50 mt-1 block w-full min-w-[275px]  rounded-lg border border-gray-500 p-2.5
                  text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500
                   dark:border-0 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:ring-gray-400
                     `}
            >
              {farms.map((s: Farm) => (
                <option value={s.farmId} key={s.farmId}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <p>Total Selected: {selectedAlpacas?.length}</p>
      <div className='w-full'>
        <label className='flex items-center gap-2 dark:text-gray-300'>
          <IndeterminateCheckbox
            {...{
              checked,
              indeterminate,
              onChange: (e) =>
                selectAllChange(
                  e.target.checked ? { checked: true } : { checked: false },
                ),
            }}
          />
          Select All {}
        </label>
        <div className='grid grid-cols-[repeat(auto-fit,minmax(325px,1fr))] gap-1 py-2'>
          {alpacaSelections.map((a) => {
            return (
              <label
                key={a.alpacaId}
                className='flex items-center gap-2 dark:text-gray-300'
              >
                <input
                  type='checkbox'
                  checked={selectedAlpacas.some((sa) => sa === a.alpacaId)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedAlpacas((prev) => [...prev, a.alpacaId!]);
                    } else {
                      setSelectedAlpacas((prev) =>
                        prev.filter((sa) => sa !== a.alpacaId),
                      );
                    }
                  }}
                />
                {a.name}
              </label>
            );
          })}
        </div>
      </div>

      <div className='flex justify-evenly gap-2'>
        <button
          type='button'
          onClick={() =>
            setSearchParams({ isOpen: 'false' }, { replace: true })
          }
          className='my-2 w-full max-w-[275px] rounded-md border border-slate-950 bg-transparent p-2 text-xs font-bold uppercase hover:border-gray-400 hover:text-gray-400 dark:border-gray-500 dark:hover:border-gray-300 dark:hover:text-gray-300'
        >
          Cancel
        </button>
        <button
          type='button'
          onClick={() => onHandleSubmit()}
          className='my-2 w-full max-w-[275px] rounded-md border border-slate-950 bg-transparent p-2 text-xs font-bold uppercase hover:border-gray-400 hover:text-gray-400 dark:border-gray-500 dark:hover:border-gray-300 dark:hover:text-gray-300'
        >
          Submit
        </button>
      </div>
    </div>
  );
}
